import { ReactElement, MouseEventHandler } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/custom/button";
import { PiDotsThree } from "react-icons/pi";
import { Link } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.tsx";
import { useTranslation } from "react-i18next";
import { useTooltipProps } from "@/components/custom/tooltip.tsx";

export type TargetOptions = "_blank";
type LinkOptions = {
  target?: TargetOptions;
};
type LinkProps = {
  to: string;
  options?: LinkOptions;
};

export interface EdgifyFloatingMenuRow {
  text: string | ReactElement;
  icon?: string;
  disabled?: boolean;
  tooltip?: string;
  onClick?: MouseEventHandler;
  link?: LinkProps;
  confirmMessage?: string;
  confirmTitle?: string;
  confirmActionText?: string;
}

export interface EdgifyFloatingMenuProps {
  menuRows: EdgifyFloatingMenuRow[];
}
// function fixAnchor(link) {
//   console.log(link);
//   const newWindow = window.open(
//     "https://docs.edgify.ai/release-notes/agent/",
//     "_blank",
//   );
//   if (newWindow) {
//     // Ensure navigation to the anchor in the new tab
//     // newWindow.onload = () => {
//     //   newWindow.location.hash = "agent-version-217141";
//     // };
//     setTimeout(() => {
//       newWindow.document
//         .getElementById("#agent-version-217141")
//         ?.scrollIntoView({ behavior: "smooth", block: "start" });
//     }, 100);
//   }
//   return false;
//   // Prevent default action if handled by script }
// }

export const FloatingMenu = ({ menuRows }: EdgifyFloatingMenuProps) => {
  const { t } = useTranslation();
  const tooltipProps = useTooltipProps();

  const renderMenuRows = () => {
    return menuRows.map((row, index) => {
      const handleConfirm = (event: any) => {
        if (row.onClick) {
          row.onClick(event);
        } else if (row.link) {
          if (row.link.options?.target === "_blank") {
            window.open(row.link.to, row.link.options.target);
          } else {
            window.location.href = row.link.to;
          }
        }
      };

      const content = (
        <div>
          <span className="mr-2">{row.icon}</span>
          {row.link ? (
            row.link.options?.target === "_blank" ? (
              <a href={row.link.to} target="_blank">
                {row.text}
              </a>
            ) : (
              <Link to={row.link.to}>{row.text}</Link>
            )
          ) : (
            <span
              {...tooltipProps}
              data-tooltip-content={row.tooltip}
              className={row.tooltip ? "pointer-events-auto" : undefined}
            >
              {row.text}
            </span>
          )}
        </div>
      );

      if (row.confirmMessage) {
        return (
          <AlertDialog key={index}>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                disabled={row.disabled}
                onSelect={(e) => e.preventDefault()}
              >
                {content}
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {row.confirmTitle ?? t("areYouSure")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {row.confirmMessage}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  {row.confirmActionText ?? t("delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        );
      }

      // Otherwise render normally.
      return (
        <DropdownMenuItem
          key={index}
          disabled={row.disabled}
          onClick={row.onClick}
        >
          {content}
        </DropdownMenuItem>
      );
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon">
          <PiDotsThree size={16} color="#020817" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="left" /*className="w-52"*/>
        <DropdownMenuGroup>{renderMenuRows()}</DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
